import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { FiUsers, FiFileText, FiMail } from 'react-icons/fi';
import { motion } from 'framer-motion';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    newContacts: 0
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch blogs
      const blogsResponse = await axios.get('/api/blogs/admin', config);
      const blogs = blogsResponse.data;
      
      // Fetch contacts
      const contactsResponse = await axios.get('/api/contact', config);
      const contacts = contactsResponse.data;

      // Update stats
      setStats({
        totalBlogs: blogs.length,
        publishedBlogs: blogs.filter(blog => blog.status === 'published').length,
        newContacts: contacts.filter(contact => contact.status === 'new').length
      });

      // Set recent items
      setRecentBlogs(blogs.slice(0, 5));
      setRecentContacts(contacts.slice(0, 5));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      setLoading(false);
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    }
  };

  const StatCard = ({ title, value, icon, trend }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{title}</p>
          <h3 className="text-2xl font-bold mt-2 text-gray-900 dark:text-white">{value}</h3>
        </div>
        <div className="bg-primary/10 p-3 rounded-full">
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center">
          <span className={`text-sm ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
          <span className="text-gray-400 text-sm ml-2">from last month</span>
        </div>
      )}
    </motion.div>
  );

  const ActivityCard = ({ title, items }) => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg"
    >
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{title}</h3>
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div>
              <p className="text-gray-900 dark:text-white font-medium">{item.title}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{item.date}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              item.status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
              item.status === 'draft' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
              'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            }`}>
              {item.status}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
        <div className="text-center text-red-600 dark:text-red-400">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <StatCard
            title="Total Blogs"
            value={stats.totalBlogs}
            icon={<FiFileText className="w-6 h-6 text-primary" />}
          />
          <StatCard
            title="Published Blogs"
            value={stats.publishedBlogs}
            icon={<FiFileText className="w-6 h-6 text-primary" />}
          />
          <StatCard
            title="New Messages"
            value={stats.newContacts}
            icon={<FiMail className="w-6 h-6 text-primary" />}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ActivityCard
            title="Recent Blog Posts"
            items={recentBlogs.map(blog => ({
              title: blog.title,
              date: new Date(blog.createdAt).toLocaleDateString(),
              status: blog.status
            }))}
          />
          <ActivityCard
            title="Recent Messages"
            items={recentContacts.map(contact => ({
              title: contact.name,
              date: new Date(contact.createdAt).toLocaleDateString(),
              status: contact.status
            }))}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
